import { Component, output, input, inject, effect, signal, ViewChild, OnInit } from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ProgressBarModule } from 'primeng/progressbar';
import { MicSettings, MicVadComponent, AudioRecording, DCMicComponent } from '@dataclouder/ngx-mic';
import { ChatMessage, ChatRole, IConversationFlow } from '../../../models/agent.models';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { ConversationService } from '../../../services/conversation.service';
import { EvaluationService } from '../../../services/evaluation.service';
import { CONVERSATION_AI_TOKEN } from '../../../models/agent-card-abstract.model';

@Component({
  selector: 'dc-chat-footer',
  standalone: true,
  imports: [ReactiveFormsModule, ProgressBarModule, TextareaModule, ButtonModule, MicVadComponent, DCMicComponent],
  templateUrl: './chat-footer.component.html',
  styleUrls: ['./chat-footer.component.scss'],
})
export class ChatFooterComponent {
  // Services
  private conversationService = inject(ConversationService);
  private evaluationService = inject(EvaluationService);
  private agentCardService = inject(CONVERSATION_AI_TOKEN);
  // ViewChild
  @ViewChild(MicVadComponent) private micComponent!: MicVadComponent;

  // Inputs
  readonly isAIThinking = input(false);
  readonly flow = input<IConversationFlow>();

  readonly micSettings = input<MicSettings>({ micMode: 'recognition', lang: 'en' });

  // Outputs
  readonly sendMessage = output<ChatMessage | null>();
  readonly textInputChanged = output<string>();

  // readonly micFinishedEvent = output<any>();
  public isGettingTranscription = false;
  public isUserTalking = false;
  public micStatus = '';

  // Signal to control mic resumption
  private shouldResumeMic = signal<boolean>(false);

  public chatInputControl: FormControl = new FormControl();

  private lastUserMessageId: string | null = null;

  constructor() {
    // Watch for audio completion and set the shouldResumeMic signal
    effect(() => {
      const audioStatus = this.conversationService.currentAudioStatus();

      // If audio has completed playing and mic is in paused state, resume recording
      if (audioStatus?.completed && this.micStatus === 'paused') {
        console.log('Audio playback completed, resuming mic recording');
        this.shouldResumeMic.set(true);

        // Reset after a short delay to avoid continuous triggering
        setTimeout(() => {
          this.shouldResumeMic.set(false);
        }, 100);
      }
    });
  }

  /**
   * Method used by the template to determine if the mic should continue listening
   */
  public shouldContinueListening(): boolean {
    return this.shouldResumeMic();
  }

  // Get score from evaluation service
  public score = this.evaluationService.scoreSignal;

  /**
   * Sets the input text in the textarea
   * @param text The text to set
   */
  public setInputText(text: string): void {
    this.chatInputControl.setValue(text);
    this.textInputChanged.emit(text);
  }

  /**
   * Handles the mic finished event
   * @param eventBlob The blob event from the mic component
   */
  public micFinished(eventBlob: any): void {
    // this.micFinishedEvent.emit(eventBlob);
  }

  /**
   * Sends the user message
   */
  public async prepareUserMsnAndSend(): Promise<void> {
    if (this.isAIThinking() || !this.chatInputControl.value) {
      return;
    }

    const text = this.chatInputControl.value;
    const message: ChatMessage = { content: text, role: ChatRole.User };
    this.chatInputControl.setValue('');

    this.conversationService.prepareUserMsnAndSend(message);
  }

  public handleAudioRecorded(event: AudioRecording): void {
    console.log(event);
    this.onMicFinished(event.blob);
  }

  /**
   * Handles the mic status changed event
   * @param event The status event from the mic component
   */
  public handleMicStatusChanged(event: { status: string; description: string }): void {
    console.log('Mic status changed:', event);
    this.micStatus = event.status;

    if (event.status === 'listening' && this.lastUserMessageId === null) {
      this.lastUserMessageId = this.conversationService.createNewUserMessage();
    }

    // Update isUserTalking based on the status
    this.isUserTalking = event.status === 'recording';
  }

  public async onMicFinished(eventBlob: Blob | any): Promise<void> {
    if (!(eventBlob instanceof Blob)) {
      return;
    }

    this.isUserTalking = false;
    this.isGettingTranscription = true;

    try {
      const message: ChatMessage = {
        content: '',
        role: ChatRole.User,
        audioUrl: URL.createObjectURL(eventBlob),
        isLoading: true,
      };
      // Instead of creating message, eneble animation user is spacking

      // const messageId = this.conversationService.addMessage(message);
      // Get transcription from audio
      const transcription = await this.agentCardService.getAudioTranscriptions(eventBlob, null);

      // Create updated message with transcription and isLoading set to false
      const updatedMessage = {
        ...message,
        content: transcription ? transcription.text : '',
        transcription: transcription || undefined,
        isLoading: false,
      };

      // The evaluation will happen automatically in the conversation service
      await this.conversationService.prepareUserMsnAndSend(updatedMessage, this.lastUserMessageId);
      this.lastUserMessageId = null;
    } finally {
      this.isGettingTranscription = false;
    }
  }

  // public setScore(score: number): void {
  //   this.evaluationService.setScore(100);
  // }

  /**
   * Stops the microphone recording if the component is available.
   */
  public stopMic(): void {
    if (this.micComponent) {
      this.micComponent.stopRecording();
    }
  }
}
